import {
  createTopoloAuth,
  decodeJwtPayload,
  getTokenExpiration,
  isTokenExpired,
  isTokenFullyExpired,
} from '@topolo-io/app-shell';
import type { AppUser, AppAuthTokens, TopoloAuth } from '@topolo-io/app-shell';
import type { BrowserSessionRefreshResult } from '@topolo-io/auth-client';
import { getAppId } from '@topolo-io/app-shell';

// CRM keeps these names for its existing import sites; the canonical user/token
// shapes now come from @topolo-io/app-shell.
export type AuthUser = AppUser;
export type AuthTokens = AppAuthTokens;

type CrmSessionRefreshResult =
  | (Extract<BrowserSessionRefreshResult, { ok: true }> & { user?: AuthUser })
  | Extract<BrowserSessionRefreshResult, { ok: false }>;

export const AUTH_BOOTSTRAP_TIMEOUT_MS = 3_000;
export const AUTH_PERMISSION_HYDRATION_TIMEOUT_MS = 5_000;

export const sessionStorageRestoreEnabled =
  import.meta.env.VITE_SESSION_STORAGE_RESTORE !== 'false';

const AUTH_SERVICE_URL = import.meta.env.VITE_TOPOLO_AUTH_URL || import.meta.env.VITE_AUTH_URL;
if (!AUTH_SERVICE_URL) {
  throw new Error('VITE_TOPOLO_AUTH_URL or VITE_AUTH_URL is required');
}

/**
 * The canonical Topolo auth instance, built on @topolo-io/app-shell's
 * `createTopoloAuth` (which wraps auth-client's browser session controller).
 * Replaces CRM's previously hand-rolled wrapper.
 */
const sharedAuthClient: TopoloAuth = createTopoloAuth({
  authServiceUrl: AUTH_SERVICE_URL,
  appId: getAppId(),
  devSessionKey: 'topolo_access_token_mem',
  callbackPath: '/auth/callback',
  sessionStorageRestoreEnabled,
  debug: Boolean(import.meta.env.DEV),
});

function withAuthTimeout<T>(operation: Promise<T>, timeoutMs: number, fallback: T): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<T>(resolve => {
    timeoutId = setTimeout(() => resolve(fallback), timeoutMs);
  });
  return Promise.race([operation, timeout]).finally(() => {
    if (timeoutId) clearTimeout(timeoutId);
  });
}

export const authClient: TopoloAuth = {
  ...sharedAuthClient,
  refreshSession(options = {}) {
    const timeoutMs =
      'timeoutMs' in options && typeof options.timeoutMs === 'number'
        ? options.timeoutMs
        : AUTH_BOOTSTRAP_TIMEOUT_MS;
    const refreshOptions = { ...options, timeoutMs } as Parameters<TopoloAuth['refreshSession']>[0];
    return withAuthTimeout<CrmSessionRefreshResult>(
      sharedAuthClient.refreshSession(refreshOptions),
      timeoutMs,
      {
        ok: false,
        code: 'TRANSIENT_FAILURE',
        logout: false,
        message: String('Auth refresh timed out'),
      } as CrmSessionRefreshResult
    );
  },
  fetchCurrentUser(accessToken?: string | null) {
    return withAuthTimeout(
      sharedAuthClient.fetchCurrentUser(accessToken),
      AUTH_PERMISSION_HYDRATION_TIMEOUT_MS,
      null
    );
  },
  fetchCurrentUserResult(accessToken?: string | null) {
    return withAuthTimeout(
      sharedAuthClient.fetchCurrentUserResult(accessToken),
      AUTH_PERMISSION_HYDRATION_TIMEOUT_MS,
      { user: null, status: null }
    );
  },
};

export const accessTokenMemory = authClient.accessTokenMemory;
export const accessTokenSession = authClient.accessTokenSession;
export const buildUserFromAccessToken = authClient.buildUserFromAccessToken;

export { decodeJwtPayload, getTokenExpiration, isTokenExpired, isTokenFullyExpired };
